import { useCallback, useEffect, useRef, useState } from 'react';
import { RecordFile } from '../../services/boxFilesService';
import { listFiles, RecordFilesSource, sourceKey } from './recordFilesSource';

export type RecordFilesState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; files: RecordFile[] };

/**
 * Loads the file list for a source. A monotonic request id drops responses that arrive
 * after the source changed or a newer reload started, so an older list never wins.
 * Changing `refreshToken` refetches (e.g. another panel for the same record changed its files).
 */
export const useRecordFiles = (source: RecordFilesSource, refreshToken?: number | string) => {
    const key = sourceKey(source);
    const sourceRef = useRef(source);
    sourceRef.current = source;

    const [state, setState] = useState<RecordFilesState>({ status: 'loading' });
    const stateRef = useRef(state);
    stateRef.current = state;
    const requestId = useRef(0);

    const load = useCallback(() => {
        const id = ++requestId.current;
        setState({ status: 'loading' });
        listFiles(sourceRef.current)
            .then(files => {
                if (id === requestId.current) setState({ status: 'ready', files });
            })
            .catch(() => {
                if (id === requestId.current) setState({ status: 'error' });
            });
    }, []);

    // Invalidates in-flight responses (source change, refresh or unmount).
    const invalidate = useCallback(() => {
        requestId.current++;
    }, []);

    useEffect(() => {
        load();
        return invalidate;
    }, [key, refreshToken, load, invalidate]);

    const addFile = useCallback((file: RecordFile) => {
        setState(s => (s.status === 'ready' ? { status: 'ready', files: [...s.files, file] } : s));
    }, []);

    // Without a loaded list there is nothing to remove from (and a list in flight may still contain the
    // file), so refetch instead.
    const removeFile = useCallback((fileId: string) => {
        if (stateRef.current.status !== 'ready') {
            load();
            return;
        }
        setState(s => (s.status === 'ready' ? { status: 'ready', files: s.files.filter(f => f.id !== fileId) } : s));
    }, [load]);

    return { state, reload: load, addFile, removeFile };
};
